import React, { useState, useEffect } from 'react';
import { createTestimonial, updateTestimonial, getCategories } from '../../services/api';
import { FormInput } from '../components/FormInput';
import { useImageUpload } from '../hooks/useImageUpload';
import { getFullImageUrl } from '../utils/helpers';
import toast from 'react-hot-toast';

const TestimonialForm = ({ editingItem, onClose, onRefresh }) => {
  const [testimonialCategories, setTestimonialCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  
  const [formData, setFormData] = useState({
    name: editingItem?.name || '',
    position: editingItem?.position || '',
    company: editingItem?.company || '',
    category: editingItem?.category || '',
    content: editingItem?.content || '',
    rating: editingItem?.rating || 5,
    avatar: editingItem?.avatar || '',
    order: editingItem?.order || 0,
    isActive: editingItem?.isActive !== false
  });
  const [loading, setLoading] = useState(false);
  const { imageFile, uploading, preview, handleImageChange, uploadImage } = useImageUpload();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      let categoriesArray = [];
      
      // Filter only testimonial type categories
      if (response && response.data && Array.isArray(response.data)) {
        categoriesArray = response.data.filter(cat => cat.type === 'testimonial');
      } else if (response && Array.isArray(response)) {
        categoriesArray = response.filter(cat => cat.type === 'testimonial');
      }
      
      setTestimonialCategories(categoriesArray);
      
      // Set default category if editing and category exists
      if (editingItem?.category && categoriesArray.length > 0 && !formData.category) {
        const existingCategory = categoriesArray.find(c => c.name === editingItem.category);
        if (existingCategory) {
          setFormData(prev => ({ ...prev, category: existingCategory.name }));
        }
      } else if (!editingItem && categoriesArray.length > 0 && !formData.category) {
        // Set first category as default for new form
        setFormData(prev => ({ ...prev, category: categoriesArray[0].name }));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleAddNewCategory = async () => {
    if (!newCategory.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://inteliheads.in/api'}/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          name: newCategory.trim(),
          type: 'testimonial',
          order: testimonialCategories.length + 1,
          isActive: true
        })
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success('Category added successfully');
        setTestimonialCategories([...testimonialCategories, { _id: result.data._id, name: newCategory.trim(), type: 'testimonial' }]);
        setFormData({ ...formData, category: newCategory.trim() });
        setNewCategory('');
        setShowNewCategoryInput(false);
      } else {
        toast.error(result.message || 'Failed to add category');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Error adding category');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    let avatar = formData.avatar;
    if (imageFile) avatar = await uploadImage();
    
    const submitData = {
      name: formData.name,
      position: formData.position,
      company: formData.company,
      category: formData.category,
      content: formData.content,
      rating: parseInt(formData.rating),
      avatar: avatar,
      order: parseInt(formData.order),
      isActive: formData.isActive
    };
    
    try {
      if (editingItem && editingItem._id) {
        await updateTestimonial(editingItem._id, submitData);
        toast.success('Testimonial updated successfully');
      } else {
        await createTestimonial(submitData);
        toast.success('Testimonial created successfully');
      }
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast.error(error.message || 'Error saving testimonial');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (categoryName) => {
    const category = testimonialCategories.find(c => c.name === categoryName);
    return category?.icon || '💬';
  };

  if (loadingCategories) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
        <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '500px', width: '90%', textAlign: 'center' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#2C7A7B' }}></i>
          <p style={{ marginTop: '1rem' }}>Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '600px', width: '90%', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#0A192F', margin: 0 }}>{editingItem ? 'Edit Testimonial' : 'Add New Testimonial'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          {/* Name and Position - First Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <FormInput label="Name *" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            <FormInput label="Position" name="position" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} />
          </div>
          
          {/* Category Selection - Second Row (After Name/Position) */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Category *</label>
            <select
              required
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px', marginBottom: '0.5rem' }}
            >
              <option value="">-- Select a category --</option>
              {testimonialCategories.length === 0 ? (
                <option value="">No categories available. Please add a category first.</option>
              ) : (
                testimonialCategories.map((cat) => (
                  <option key={cat._id} value={cat.name}>
                    {cat.icon} {cat.name}
                  </option>
                ))
              )}
            </select>
            
            {!showNewCategoryInput ? (
              <button 
                type="button"
                onClick={() => setShowNewCategoryInput(true)}
                style={{
                  padding: '0.4rem 0.8rem',
                  background: 'transparent',
                  color: '#2C7A7B',
                  border: '1px solid #2C7A7B',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem'
                }}
              >
                <i className="fas fa-plus"></i> Add New Category
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Enter new category name"
                  style={{ flex: 1, padding: '0.5rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleAddNewCategory}
                  style={{ padding: '0.5rem 1rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewCategoryInput(false);
                    setNewCategory('');
                  }}
                  style={{ padding: '0.5rem 1rem', background: '#5A6E7F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            )}
            
            {formData.category && testimonialCategories.length > 0 && (
              <small style={{ color: '#2C7A7B', display: 'block', marginTop: '0.25rem' }}>
                <i className="fas fa-info-circle"></i> Selected: {getCategoryIcon(formData.category)} {formData.category}
              </small>
            )}
          </div>
          
          {/* Company and Rating - Third Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <FormInput label="Company" name="company" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
            <FormInput label="Rating (1-5)" name="rating" type="number" min="1" max="5" value={formData.rating} onChange={(e) => setFormData({ ...formData, rating: e.target.value })} />
          </div>
          
          {/* Display Order */}
          <FormInput label="Display Order" name="order" type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: e.target.value })} />
          
          {/* Testimonial Content */}
          <FormInput label="Testimonial *" name="content" type="textarea" rows="4" value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} required />
          
          {/* Avatar Image */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Avatar Image</label>
            {preview && <img src={preview} alt="Preview" style={{ width: '60px', height: '60px', borderRadius: '50%', marginBottom: '0.5rem', objectFit: 'cover' }} />}
            {formData.avatar && !preview && <img src={getFullImageUrl(formData.avatar)} alt="Current" style={{ width: '60px', height: '60px', borderRadius: '50%', marginBottom: '0.5rem', objectFit: 'cover' }} />}
            <input type="file" accept="image/*" onChange={(e) => handleImageChange(e.target.files[0])} style={{ width: '100%', marginBottom: '0.5rem' }} />
            <input 
              type="text" 
              value={formData.avatar} 
              onChange={(e) => setFormData({ ...formData, avatar: e.target.value })} 
              placeholder="Avatar URL" 
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px', marginTop: '0.5rem' }} 
            />
          </div>
          
          {/* Active Status */}
          <FormInput label="Active" name="isActive" type="checkbox" value={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />
          
          {/* Submit Buttons */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button type="submit" disabled={loading || uploading} style={{ flex: 1, padding: '0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              {loading || uploading ? 'Saving...' : 'Save Testimonial'}
            </button>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '0.75rem', background: '#5A6E7F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TestimonialForm;